/**
 * The tick list (BUILD-SPEC.md §7, phase "ticklist"): everyone the meeting
 * is with, all checked by default; untick the absent; settings for
 * seconds-per-person and grace; Start shuffles the checked entries and
 * hands them to the controller.
 */
import { useId, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Slider,
  Text,
  tokens,
} from "@fluentui/react-components";
import type { QueueEntry, Settings } from "../engine";
import { shuffle } from "../engine";
import type { AppOpenUser } from "../contract";
import type { RosterMember } from "../roster/members";
import { EMPTY_ROSTER_MESSAGE } from "../roster/members";
import { AddPerson } from "./AddPerson";

/** A row in the tick list, before a standup exists — a lighter-weight shape
 * than `QueueEntry` since nothing here has "waiting"/"speaking" status yet. */
export interface TickItem {
  key: string;
  userId: string | null;
  displayName: string;
  source: "invited" | "manual";
}

export function itemsFromRoster(members: RosterMember[]): TickItem[] {
  return members.map((m) => ({ key: m.userId, userId: m.userId, displayName: m.displayName, source: "invited" }));
}

function newManualKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `manual:${crypto.randomUUID()}`;
  }
  return `manual:${Math.random().toString(36).slice(2)}`;
}

/** Pure: builds the shuffled start order from the ticked items. Exported so
 * it can be unit-tested (Node environment) without rendering anything. */
export function buildStartOrder(
  items: TickItem[],
  checkedKeys: ReadonlySet<string>,
  rng?: () => number
): QueueEntry[] {
  const checked = items.filter((item) => checkedKeys.has(item.key));
  const entries: QueueEntry[] = checked.map((item) => ({
    key: item.key,
    userId: item.userId,
    displayName: item.displayName,
    source: item.source,
    status: "waiting",
    joinedLate: false,
    spentSeconds: 0,
    extensionSeconds: 0,
  }));
  return shuffle(entries, rng);
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  openMarker: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
  },
  settings: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
});

export interface TickListProps {
  members: RosterMember[];
  /** Non-null when the roster fetch failed — every failure mode collapses
   * to this one message (BUILD-SPEC.md §6). */
  memberFetchError: string | null;
  appOpenUsers: AppOpenUser[];
  settings: Settings;
  onSettingsChange: (next: Settings) => void;
  onStart: (order: QueueEntry[], settings: Settings) => void;
}

export function TickList({
  members,
  memberFetchError,
  appOpenUsers,
  settings,
  onSettingsChange,
  onStart,
}: TickListProps) {
  const styles = useStyles();
  const secondsFieldId = useId();
  const graceFieldId = useId();

  const rosterItems = useMemo(() => itemsFromRoster(members), [members]);
  const [manualItems, setManualItems] = useState<TickItem[]>([]);
  const items = useMemo(() => [...rosterItems, ...manualItems], [rosterItems, manualItems]);

  // All ticked by default (BUILD-SPEC.md §7). Re-derives whenever the
  // roster changes (e.g. the fetch resolves after first render).
  const [uncheckedKeys, setUncheckedKeys] = useState<ReadonlySet<string>>(new Set());
  const checkedKeys = useMemo(
    () => new Set(items.map((i) => i.key).filter((k) => !uncheckedKeys.has(k))),
    [items, uncheckedKeys]
  );

  const openUserIds = useMemo(() => new Set(appOpenUsers.map((u) => u.userId)), [appOpenUsers]);

  function toggle(key: string, checked: boolean): void {
    setUncheckedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function addManualPreStart(displayName: string): void {
    setManualItems((prev) => [...prev, { key: newManualKey(), userId: null, displayName, source: "manual" }]);
  }

  const checkedCount = checkedKeys.size;
  const startDisabled = checkedCount === 0;

  return (
    <div className={styles.root}>
      {memberFetchError && (
        <MessageBar intent="warning">
          <MessageBarBody>{EMPTY_ROSTER_MESSAGE}</MessageBarBody>
        </MessageBar>
      )}

      <ol className={styles.list}>
        {items.map((item) => (
          <li key={item.key} className={styles.row}>
            <Checkbox
              label={item.displayName}
              checked={checkedKeys.has(item.key)}
              onChange={(_, data) => toggle(item.key, data.checked === true)}
            />
            {item.userId !== null && openUserIds.has(item.userId) && (
              <span className={styles.openMarker} title="Has the app open">
                ● open
              </span>
            )}
          </li>
        ))}
      </ol>

      <AddPerson onAddManual={addManualPreStart} onAddInvited={() => {}} label="Add person" />

      <div className={styles.settings}>
        <Field label={`Seconds per person: ${settings.secondsPerPerson}`}>
          <Slider
            id={secondsFieldId}
            min={15}
            max={600}
            step={15}
            value={settings.secondsPerPerson}
            onChange={(_, data) => onSettingsChange({ ...settings, secondsPerPerson: data.value })}
          />
        </Field>
        <Field label={`Grace seconds: ${settings.graceSeconds}`}>
          <Slider
            id={graceFieldId}
            min={0}
            max={120}
            step={5}
            value={settings.graceSeconds}
            onChange={(_, data) => onSettingsChange({ ...settings, graceSeconds: data.value })}
          />
        </Field>
      </div>

      <Button
        appearance="primary"
        disabled={startDisabled}
        onClick={() => onStart(buildStartOrder(items, checkedKeys), settings)}
      >
        Start
      </Button>
      {startDisabled && <Text role="status">tick at least one person</Text>}
    </div>
  );
}
